import {
	Box,
	Card,
	CardContent,
	Skeleton,
	Table,
	TableBody,
	TableContainer,
	TableHead,
	Typography,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { StyledTableCell, StyledTableRow } from 'ui/StyledTable';
import { InfoDialButton } from 'ui/InfoDialButton';
import { useSelector } from 'react-redux';
import { getShipmentsOrder, getShipmentsOrderIsLoad } from 'redux/reducers/shipments/selectors';

export type TShipmentDetailsTableItem = {
	name: string;
	currency: string;
	original: number | null;
	preliminary: number | null;
	actual: number | null;
	toBePaid: number | null;
};

const dataTemplate: TShipmentDetailsTableItem[] = [
	{
		name: 'Merchandise Cost',
		currency: 'CHF',
		original: null,
		preliminary: null,
		actual: null,
		toBePaid: null,
	},
	{
		name: 'Delivery Cost',
		currency: 'CHF',
		original: null,
		preliminary: null,
		actual: null,
		toBePaid: null,
	},
	{
		name: 'Customs Duties',
		currency: 'CHF',
		original: null,
		preliminary: null,
		actual: null,
		toBePaid: null,
	},
	{
		name: 'VAT Amount',
		currency: 'CHF',
		original: null,
		preliminary: null,
		actual: null,
		toBePaid: null,
	},
	{
		name: 'Customs Procedures Processing Fee',
		currency: 'CHF',
		original: null,
		preliminary: null,
		actual: null,
		toBePaid: null,
	},
];

export const ShipmentDetails: FC = () => {
	const order = useSelector(getShipmentsOrder);
	const orderIsLoading = useSelector(getShipmentsOrderIsLoad);

	const [data, setData] = useState<TShipmentDetailsTableItem[]>(dataTemplate);

	const paymentCurrency = order?.order?.paymentCurrency ?? 'EUR';

	useEffect(() => {
		if (!order || !order.orderShipment) return;
		const shipment = order.orderShipment;
		const dataTmp = [...dataTemplate];
		dataTmp[0].original = shipment.initialMerchAmount;
		dataTmp[0].preliminary = shipment.preliminaryMerchAmount;
		dataTmp[0].actual = shipment.actualMerchAmount;
		dataTmp[1].original = shipment.initialDeliveryCost;
		dataTmp[1].preliminary = shipment.preliminaryDeliveryCost;
		dataTmp[1].actual = shipment.actualDeliveryCost;
		dataTmp[2].original = shipment.initialDuties;
		dataTmp[2].preliminary = shipment.preliminaryDuties;
		dataTmp[2].actual = shipment.actualDuties;
		dataTmp[2].toBePaid = shipment.actualEurDuties;
		dataTmp[3].original = shipment.initialVat;
		dataTmp[3].preliminary = shipment.preliminaryVat;
		dataTmp[3].actual = shipment.actualVat;
		dataTmp[3].toBePaid = shipment.actualEurVat;
		dataTmp[4].actual = shipment.actualCustomsFee;
		dataTmp[4].toBePaid = shipment.actualEurCustomsFee;
		setData(dataTmp);
	}, [order]);

	return (
		<div>
			{!!order && !orderIsLoading ? (
				<Card
					sx={{
						mb: 2,
						'&': {
							borderRadius: '12px',
						},
					}}
				>
					<CardContent>
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
							<Typography sx={{ fontSize: 14 }} color="primary" gutterBottom>
								Shipment Details
							</Typography>
						</Box>
						<Box
							sx={{
								mt: 1.5,
								justifyContent: 'space-between',
								alignItems: 'center',
								border: '1px solid #00000020',
								borderRadius: '12px',
							}}
						>
							<TableContainer
								sx={{
									borderRadius: '12px',
								}}
							>
								<Table>
									<TableHead>
										<StyledTableRow>
											<StyledTableCell
												align="left"
												style={{
													padding: '2px 8px 2px 8px',
													verticalAlign: 'top',
													backgroundColor: '#006293',
												}}
											/>
											<StyledTableCell
												align="right"
												style={{
													padding: '2px 8px 2px 8px',
													verticalAlign: 'top',
													backgroundColor: '#006293',
												}}
											>
												Original
											</StyledTableCell>
											<StyledTableCell
												align="right"
												style={{
													padding: '2px 8px 2px 8px',
													verticalAlign: 'top',
													backgroundColor: '#006293',
												}}
											>
												Preliminary
											</StyledTableCell>
											<StyledTableCell
												align="right"
												style={{
													padding: '2px 8px 2px 8px',
													verticalAlign: 'top',
													backgroundColor: '#006293',
												}}
											>
												Actual
											</StyledTableCell>
											<StyledTableCell
												align="right"
												style={{
													padding: '2px 8px 2px 8px',
													verticalAlign: 'top',
													backgroundColor: '#006293',
													borderRadius: '0 12px 0 0',
												}}
											>
												To Be Paid
												<InfoDialButton
													color="secondary"
													text="Below values will be included into the invoices: for the IOSS order to the regular Invoice after IOSS report will be generated, for the DDP orders to the special invoice issue twice per month"
												/>
											</StyledTableCell>
										</StyledTableRow>
									</TableHead>
									<TableBody>
										{data.map((row) => (
											<StyledTableRow key={row.name}>
												<StyledTableCell>
													{row.name}
													{row.name === 'Delivery Cost' && (
														<InfoDialButton text="Delivery amount added to the Merchandise amount during customs clearance process for simplification of the procedure" />
													)}
												</StyledTableCell>
												<StyledTableCell align="right">
													{row.original === null ? (
														'—'
													) : (
														<AmountAndCurrency
															amount={row.original ?? 0}
															currency={paymentCurrency}
														/>
													)}
												</StyledTableCell>
												<StyledTableCell align="right">
													{row.preliminary === null ? (
														'—'
													) : (
														<AmountAndCurrency
															amount={row.preliminary ?? 0}
															currency={paymentCurrency}
														/>
													)}
												</StyledTableCell>
												<StyledTableCell align="right">
													{row.actual === null ? (
														'—'
													) : (
														<AmountAndCurrency
															amount={row.actual ?? 0}
															currency={paymentCurrency}
														/>
													)}
												</StyledTableCell>
												<StyledTableCell align="right">
													{row.toBePaid === null ? (
														'—'
													) : (
														<AmountAndCurrency amount={row.toBePaid ?? 0} currency="EUR" />
													)}
												</StyledTableCell>
											</StyledTableRow>
										))}
									</TableBody>
									{/* )} */}
								</Table>
							</TableContainer>
						</Box>
					</CardContent>
				</Card>
			) : (
				<Skeleton variant="rectangular" height="288px" sx={{ borderRadius: '12px', mb: 2 }} />
			)}
		</div>
	);
};
